/**
 * Subscription Reducer
 * 
 * Handles state updates for subscription management.
 * Uses a reducer pattern for predictable state management.
 */

import {
  SUBSCRIPTION_ACTIONS,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_ERROR_TYPES,
  DEFAULT_SUBSCRIPTION_CONFIG
} from '../constants/subscriptionTypes';
import { DEFAULT_SUBSCRIPTION_STATE } from '../context/SubscriptionContext';

/**
 * Subscription reducer function
 * @param {Object} state - Current subscription state
 * @param {Object} action - Action to process
 * @returns {Object} New subscription state
 */
export function subscriptionReducer(state = DEFAULT_SUBSCRIPTION_STATE, action) {
  const { type, payload } = action;

  switch (type) {
    case SUBSCRIPTION_ACTIONS.CHECK_STATUS:
      return {
        ...state,
        loading: {
          ...state.loading,
          status: true
        },
        error: null
      };

    case SUBSCRIPTION_ACTIONS.CHECK_STATUS_SUCCESS:
      return {
        ...state,
        ...payload,
        loading: {
          ...state.loading,
          status: false
        },
        error: null,
        lastUpdated: new Date().toISOString(),
        cache: {
          data: payload,
          timestamp: new Date().toISOString(),
          isValid: true
        }
      };

    case SUBSCRIPTION_ACTIONS.CHECK_STATUS_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          status: false
        },
        error: {
          type: SUBSCRIPTION_ERROR_TYPES.NETWORK_ERROR,
          message: payload.message || 'Failed to check subscription status',
          details: payload.details
        }
      };

    case SUBSCRIPTION_ACTIONS.ACTIVATE:
      return {
        ...state,
        loading: {
          ...state.loading,
          activation: true
        },
        error: null
      };

    case SUBSCRIPTION_ACTIONS.ACTIVATE_SUCCESS:
      return {
        ...state,
        status: SUBSCRIPTION_STATUS.ACTIVE,
        ...payload,
        loading: {
          ...state.loading,
          activation: false,
          status: false
        },
        error: null,
        lastUpdated: new Date().toISOString(),
        cache: {
          data: { ...state, ...payload },
          timestamp: new Date().toISOString(),
          isValid: true
        }
      };

    case SUBSCRIPTION_ACTIONS.ACTIVATE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          activation: false
        },
        error: {
          type: SUBSCRIPTION_ERROR_TYPES.ACTIVATION_FAILED,
          message: payload.message || 'Failed to activate subscription',
          details: payload.details
        }
      };

    case SUBSCRIPTION_ACTIONS.DEACTIVATE:
      return {
        ...state,
        loading: {
          ...state.loading,
          deactivation: true
        },
        error: null
      };

    case SUBSCRIPTION_ACTIONS.DEACTIVATE_SUCCESS:
      return {
        ...state,
        status: SUBSCRIPTION_STATUS.INACTIVE,
        loading: {
          ...state.loading,
          deactivation: false
        },
        error: null,
        lastUpdated: new Date().toISOString(),
        cache: {
          data: { ...state, status: SUBSCRIPTION_STATUS.INACTIVE },
          timestamp: new Date().toISOString(),
          isValid: true
        }
      };

    case SUBSCRIPTION_ACTIONS.DEACTIVATE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          deactivation: false
        },
        error: {
          type: SUBSCRIPTION_ERROR_TYPES.SERVER_ERROR,
          message: payload.message || 'Failed to deactivate subscription',
          details: payload.details
        }
      };

    case SUBSCRIPTION_ACTIONS.RENEW:
      return {
        ...state,
        loading: {
          ...state.loading,
          renewal: true
        },
        error: null
      };

    case SUBSCRIPTION_ACTIONS.RENEW_SUCCESS:
      return {
        ...state,
        ...payload,
        loading: {
          ...state.loading,
          renewal: false
        },
        error: null,
        lastUpdated: new Date().toISOString(),
        cache: {
          data: { ...state, ...payload },
          timestamp: new Date().toISOString(),
          isValid: true
        }
      };

    case SUBSCRIPTION_ACTIONS.RENEW_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          renewal: false
        },
        error: {
          type: SUBSCRIPTION_ERROR_TYPES.SERVER_ERROR,
          message: payload.message || 'Failed to renew subscription',
          details: payload.details
        }
      };

    case SUBSCRIPTION_ACTIONS.CANCEL:
      return {
        ...state,
        loading: {
          ...state.loading,
          deactivation: true
        },
        error: null
      };

    case SUBSCRIPTION_ACTIONS.CANCEL_SUCCESS:
      return {
        ...state,
        status: SUBSCRIPTION_STATUS.INACTIVE,
        loading: {
          ...state.loading,
          deactivation: false,
          status: false
        },
        error: null,
        lastUpdated: new Date().toISOString(),
        cache: {
          data: { ...state, status: SUBSCRIPTION_STATUS.INACTIVE },
          timestamp: new Date().toISOString(),
          isValid: true
        }
      };

    case SUBSCRIPTION_ACTIONS.CANCEL_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          deactivation: false
        },
        error: {
          type: SUBSCRIPTION_ERROR_TYPES.SERVER_ERROR,
          message: payload.message || 'Failed to cancel subscription',
          details: payload.details
        }
      };

    case SUBSCRIPTION_ACTIONS.REFRESH:
      return {
        ...state,
        loading: {
          ...state.loading,
          status: true
        },
        error: null
      };

    case SUBSCRIPTION_ACTIONS.REFRESH_SUCCESS:
      return {
        ...state,
        ...payload,
        loading: {
          ...state.loading,
          status: false
        },
        error: null,
        lastUpdated: new Date().toISOString(),
        cache: {
          data: payload,
          timestamp: new Date().toISOString(),
          isValid: true
        }
      };

    case SUBSCRIPTION_ACTIONS.REFRESH_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          status: false
        },
        error: {
          type: SUBSCRIPTION_ERROR_TYPES.NETWORK_ERROR,
          message: payload.message || 'Failed to refresh subscription data',
          details: payload.details
        }
      };

    case SUBSCRIPTION_ACTIONS.CLEAR:
      return {
        ...DEFAULT_SUBSCRIPTION_STATE,
        cache: {
          data: null,
          timestamp: null,
          isValid: false
        }
      };

    case SUBSCRIPTION_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          ...payload
        }
      };

    case SUBSCRIPTION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: {
          type: payload.type || SUBSCRIPTION_ERROR_TYPES.UNKNOWN,
          message: payload.message || 'An unknown error occurred',
          details: payload.details
        }
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case SUBSCRIPTION_ACTIONS.INVALIDATE_CACHE:
      return {
        ...state,
        cache: {
          ...state.cache,
          isValid: false
        }
      };

    default:
      console.warn(`Unknown subscription action: ${type}`);
      return state;
  }
}

/**
 * Create subscription action creators
 */
export const subscriptionActions = {
  checkStatus: () => ({
    type: SUBSCRIPTION_ACTIONS.CHECK_STATUS
  }),

  checkStatusSuccess: (data) => ({
    type: SUBSCRIPTION_ACTIONS.CHECK_STATUS_SUCCESS,
    payload: data
  }),

  checkStatusFailure: (error) => ({
    type: SUBSCRIPTION_ACTIONS.CHECK_STATUS_FAILURE,
    payload: error
  }),

  activate: () => ({
    type: SUBSCRIPTION_ACTIONS.ACTIVATE
  }),

  activateSuccess: (data) => ({
    type: SUBSCRIPTION_ACTIONS.ACTIVATE_SUCCESS,
    payload: data
  }),

  activateFailure: (error) => ({
    type: SUBSCRIPTION_ACTIONS.ACTIVATE_FAILURE,
    payload: error
  }),

  deactivate: () => ({
    type: SUBSCRIPTION_ACTIONS.DEACTIVATE
  }),

  deactivateSuccess: () => ({
    type: SUBSCRIPTION_ACTIONS.DEACTIVATE_SUCCESS
  }),

  deactivateFailure: (error) => ({
    type: SUBSCRIPTION_ACTIONS.DEACTIVATE_FAILURE,
    payload: error
  }),

  renew: () => ({
    type: SUBSCRIPTION_ACTIONS.RENEW
  }),

  renewSuccess: (data) => ({
    type: SUBSCRIPTION_ACTIONS.RENEW_SUCCESS,
    payload: data
  }),

  renewFailure: (error) => ({
    type: SUBSCRIPTION_ACTIONS.RENEW_FAILURE,
    payload: error
  }),

  cancel: () => ({
    type: SUBSCRIPTION_ACTIONS.CANCEL
  }),

  cancelSuccess: () => ({
    type: SUBSCRIPTION_ACTIONS.CANCEL_SUCCESS
  }),

  cancelFailure: (error) => ({
    type: SUBSCRIPTION_ACTIONS.CANCEL_FAILURE,
    payload: error
  }),

  refresh: () => ({
    type: SUBSCRIPTION_ACTIONS.REFRESH
  }),

  refreshSuccess: (data) => ({
    type: SUBSCRIPTION_ACTIONS.REFRESH_SUCCESS,
    payload: data
  }),

  refreshFailure: (error) => ({
    type: SUBSCRIPTION_ACTIONS.REFRESH_FAILURE,
    payload: error
  }),

  clear: () => ({
    type: SUBSCRIPTION_ACTIONS.CLEAR
  }),

  setLoading: (loading) => ({
    type: SUBSCRIPTION_ACTIONS.SET_LOADING,
    payload: loading
  }),

  setError: (error) => ({
    type: SUBSCRIPTION_ACTIONS.SET_ERROR,
    payload: error
  }),

  clearError: () => ({
    type: SUBSCRIPTION_ACTIONS.CLEAR_ERROR
  }),

  invalidateCache: () => ({
    type: SUBSCRIPTION_ACTIONS.INVALIDATE_CACHE
  })
};
